(function () {

    /*
     * ====================================================
     * GLOBAL PRIVATE CONSTS
     * ====================================================
     */
    const _formControlClass = "o-form-control";
    const _formControlInputClass = ":is(input:not(input[type='submit']):not(input[type='checkbox']):not(input[type='hidden']), select, textarea)";
    const _hintClass = "o-form-control__hint";
    const _errorClass = "o-form-control--error";

    /*
     * =====================================================
     * HELPER FUNCTIONS
     * =====================================================
     */
    /**
     * Allows chaining of functions without passing in arguments to each new function. This one is curried.
     * @params  {Function(s)} Arbitrary number of functions. Types must line up.
     * @param  {Unknown} This depends on the type signature of the first function inside the pipe.
     * @return {Unkown}      Return value of last function inside the pipe.
     */
    const pipe = (...fns) => (val) => fns.reduce((acc, curr) => curr(acc), val);

    /**
     * Turns regular functions into curried versions of themselves.
     * @param  {Function} arg1 Function to curry
     * @param  {arr = Unknown[]} arg2 Used recursively by curry, no need to actively set this.
     * @param {args = Unknown} arg3 The arguments to the function. Can by supplied multiple at a time, or one by one.
     */
    const curry = (fn, arr = []) => (...args) =>
        ((a: any) => (a.length === fn.length ? fn(...a) : curry(fn, a)))([
            ...arr,
            ...args
        ]);

    /**
     * A more concise console.log
     * @param  {Any} arg1 Whatever message you would normally console.log
     */
    function log(...message) {
        console.log(...message);
    }

    /**
     * Manager for error state form(s) on page.
     * @return {Object}      Functions to interact with manager.
     */
    const formsErrorMapManager = (function Manager() {
        let formsMap = new Map();

        /**
         * Returns Map() of the form the input element is part of.
         * @param  {HTMLElement} arg1 Any to verify input HTMLElement
         * @return {Map(HTMLElement, boolean)}  Map of all form inputs inside the form and if they have errors.
         */
        function _getFormMap(inputElement) {
            return formsMap.get(findParentForm(inputElement));
        }

        /**
         * Adds new Map() formMap to manager.
         * @param  {HTMLElement} arg1 Form HTMLElement that has inputs to verify.
         */
        function define(form) {
            if (formsMap.has(form)) {
                return "Already exists";
            }

            formsMap.set(form, new Map());
        }

        /**
         * Sets error state of input element
         * @param  {HTMLElement} arg1 Input element.
         * @param  {Boolean} arg2 If input has errors.
         */
        function set(inputElement, error = true) {
            const formMap = _getFormMap(inputElement);
            formMap.set(inputElement, error);
        }

        /**
         * Returns error state of input element
         * @param  {HTMLElement} arg1 Input element
         * @return {Boolean}     Does input element have errors?
         */
        function get(input) {
            const formMap = _getFormMap(input);
            return formMap.get(input);
        }

        /**
         * Checks all inputs, currently visible to the user inside provided form for errors. Returns form validity.
         * @param  {HTMLElement} arg1 HTML Form element.
         * @return {Boolean}      If form is valid (true/false).
         */
        function isValid(form) {
            const formMap = formsMap.get(form);
            let valid = true;
            formMap.forEach((errors, key) => {
                if (isHidden(key)) {
                    return;
                }
                if (errors === true) {
                    valid = false;
                }
            });
            return valid;
        }

        return {
            define,
            set,
            get,
            isValid
        };
    })();

    /*
     * =====================================================================
     * VALIDATION SEGMENT
     * =====================================================================
     */
    const _validationMap = {
        valEmail: validateEmail,
        valRequired: validateRequired,
        valIban: validateIban,
        valBsn: validateBsn,
        valMaxlength: validateMaxLength,
        valMinlength: validateMinLength
    };

    /**
     * Email validator
     * @param  {HTMLElement} arg1 Input element.
     * @return {Boolean}      Input valid (true/false).
     */
    function validateEmail(inputElement) {
        const value = inputElement.value;
        return String(value)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    }

    /**
    * BSN validator
    * @param  {HTMLElement} arg1 Input element.
    * @return {Boolean}      Input valid (true/false).
    */
    function validateBsn(inputElement) {

        let bsn = inputElement.value?.replace(/\s/g, '').toUpperCase().replace(/[^0-9]/g, '');
        if (bsn.length === 8) {
            bsn = '0' + bsn;
        }

        if (!bsn || bsn.length !== 9) {
            return false;
        }

        var elevenProof = elfProefValidation(bsn);
        return elevenProof;
    }

    function elfProefValidation(value) {
        let returnValue = false
        if (!value || value?.length === 0) {
            return true
        }
        if (value === '00000000000' || value.length !== 9) {
            return false
        }
        const values = value.split('')
        const firstCharacter = parseInt(values[0], 10)
        const lastCharacter = parseInt(values[values.length - 1], 10)
        const [a, b, c, d, e, f, g, h, i] = values.map(char => parseInt(char, 10))
        let result = 9 * a + 8 * b + 7 * c + 6 * d + 5 * e + 4 * f + 3 * g + 2 * h + -1 * i;
        return result > 0 && result % 11 === 0;
    }

    /**
     * IBAN validator
     * @param  {HTMLElement} arg1 Input element.
     * @return {Boolean}      Input valid (true/false).
     */
    function validateIban(inputElement) {

        const iban = inputElement.value?.replace(/\s/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);

        if (!code || iban.length !== 18) {
            return false;
        }
        const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, function (letter) {
            return letter.charCodeAt(0) - 55;
        });
        return mod97(digits) === 1;
    }

    function mod97(string) {
        var checksum = string.slice(0, 2), fragment;
        for (var offset = 2; offset < string.length; offset += 7) {
            fragment = String(checksum) + string.substring(offset, offset + 7);
            checksum = parseInt(fragment, 10) % 97;
        }
        return checksum;
    }

    /**
     * Required validator
     * @param  {HTMLElement} arg1 Input element
     * @return {Boolean}      Input valid (true/false).
     */
    function validateRequired(inputElement) {

        const type = inputElement.type;

        switch (type) {
            case 'checkbox':
            case 'radio': {
                const wrapper = inputElement.parentNode;
                const checked = wrapper.querySelector('input:checked');
                return checked;
                break;
            }
            case 'file': {
                const control = findFormControl(inputElement);
                const files = control?.querySelectorAll('.c-file-box__file:not(.is-upload-error):not(.is-loading)');
                console.log({ control, files })
                return files.length;
                break;
            }
        }
        const value = inputElement.value;
        return !!value.length;
    }
    /**
     * Max length validator
     * @param  {HTMLElement} arg1 Input element
     * @return {Boolean}      Input valid (true/false).
     */
    function validateMaxLength(inputElement) {
        const value = inputElement.value;
        const maxLength = inputElement.getAttribute("data-val-maxlength-max");
        return value.length <= maxLength;
    }
    /**
     * Min length validator
     * @param  {HTMLElement} arg1 Input element
     * @return {Boolean}      Input valid (true/false).
     */
    function validateMinLength(inputElement) {
        const value = inputElement.value;
        const minLength = inputElement.getAttribute("data-val-minlength-min");
        return value.length >= minLength;
    }

    /**
     * Returns HTMLElement and validation errors. Checks input value against validation rules.
     * @param  {HTMLElement} arg1 Input HTMLElement to validate.
     * @return {Object}      Input HTMLElement and Error messages String[]
     */
    function findInputErrors(element) {
        const validationRules = { ...element.dataset };
        let errors: any = [];

        for (let rule of Object.keys(validationRules)) {
            const ruleInValidationMap = _validationMap[rule];
            if (ruleInValidationMap && !ruleInValidationMap(element)) {
                errors.push(validationRules[rule]);
            }
        }

        return { element, errors };
    }

    /**
     * Sets provided errors on input and update formsErrorMapManager.
     * @param  {Object} arg1 Input HTMLElement and Error messages String[];
     */
    function handleInputErrors({ element, errors }) {
        if (errors.length) {
            setError(element, errors[0]);
            addClass(findFormControl(element), _errorClass);
        }

        formsErrorMapManager.set(element, !!errors.length);
    }

    const validationPipe = pipe(findInputErrors, handleInputErrors);

    /*
     * ================================================================
     * DOM MANIPULATION
     * ================================================================
     */
    /**
     * Returns whether HTMLElement is currently hidden from users view
     * @param  {HTMLElement} arg1 Any HTMLElement to check for visibility
     * @return {Boolean}      Visible (true/false)
     */
    function isHidden(element) {
        return element.offsetParent === null;
    }

    /**
     * Adds classname to an HTMLElement.
     * @param  {HTMLElement} arg1 HTMLElement to add the class to.
     * @param  {String} arg2 Classname to add.
     */
    function addClass(element, cssClass) {
        element.classList.add(cssClass);
    }

    /**
     * Removes classname from HTMLElement.
     * @param  {HTMLElement} arg1 HTMLElement to remove the class from.
     * @param  {String} arg2 Classname to remove.
     */
    function removeClass(element, cssClass) {
        element?.classList.remove(cssClass);
    }

    /**
     * Returns whether classname exists on HTMLElement
     * @param  {HTMLElement} arg1 HTMLElement to search for classname
     * @param  {String} arg2 Classname to check for.
     * @return {Boolean}      Has class name (true/false)
     */
    function hasClass(element, classToFind) {
        return element.classList.contains(classToFind);
    }

    /**
     * Returns parent form element to provided HTMLElement
     * @param  {HTMLElement} arg1 HTMLElement to find wrapping Form for.
     * @return {HTMLElement}      Form HTMLElement
     */
    function findParentForm(element) {
        if (element.tagName === "HTML") {
            return "Not Found";
        }
        if (element.tagName !== "FORM") {
            return findParentForm(element.parentNode);
        }
        return element;
    }

    /**
     * Returns input wrapper with _formControlClass to provided HTMLElement.
     * @param  {HTMLElement} arg1 Input HTMLElement
     * @return {HTMLElement}      HTMLElement with _formControlClass.
     */
    function findFormControl(element) {
        if (element.tagName === "HTML") {
            return null;
        }
        if (!hasClass(element, _formControlClass)) {
            return findFormControl(element.parentNode);
        }

        return element;
    }

    /**
     * Returns hint div/span element attached to input.
     * @param  {HTMLElement} arg1 Input HTMLElement.
     * @return {HTMLElement}      Hint div/span
     */
    function findClosestHint(element) {
        if (hasClass(element, _hintClass)) {
            return element;
        }
        const formControl = findFormControl(element);
        const hint = formControl.querySelector(`.${_hintClass}`);

        return hint;
    }

    /**
     * Updates innerHTML of input hint with provided text.
     * @param  {HTMLElement} arg1 Any HTMLElement inside an HTMLElement with _formControlClass
     * @param  {String} arg2 Error to set in hint field.
     */
    function setError(element, error) {
        if (!(element instanceof HTMLElement)) {
            return "not found";
        }
        if (!hasClass(element, _hintClass)) {
            return setError(findClosestHint(element), error);
        }

        element.innerHTML = error;
    }

    /*
     * =======================================================================
     * GENERAL SETUP
     * =======================================================================
     */
    /**
     * Handles field validation for visible inputs on focus update.
     * @param  {Event} arg1 Focus event from eventListener
     */
    function manageFormControlInputFocus(event) {
        const eventType = event.type;
        let formControlInput = event.target;

        if (formControlInput.type === 'radio') {
            formControlInput = formControlInput.parentNode.querySelector('input');
        }

        const formControl = findFormControl(formControlInput);

        //Skips validation if input is hidden.
        if (formControl === null || isHidden(formControlInput)) {
            return;
        }

        removeClass(formControl, _errorClass);
        validationPipe(formControlInput);
    }

    /**
     * Check all inputs inside form for correct validation
     * @param  {Event} arg1 Click event from clicking the submit button.
     */
    function checkAllInputs(event) {
        event.preventDefault();
        const submit = event.target;
        const form = findParentForm(submit);
        const formControlInputs = form.querySelectorAll(
            `${_formControlInputClass}:first-of-type`
        );

        formControlInputs.forEach((formControlInput) => {
            const formControl = findFormControl(formControlInput);
            removeClass(formControl, _errorClass);
            if (isHidden(formControlInput)) {
                return;
            }
            validationPipe(formControlInput);
        });

        const valid = formsErrorMapManager.isValid(form);
        if (!valid) {
            form.querySelector(`.${_errorClass} ${_formControlInputClass}`).focus();
            return;
        }

        form.submit();

        const button = document.querySelector('[type=submit]');
        if (button) {
            (button as HTMLButtonElement).disabled = true;
            button.classList.add('u-disabled');
            button.classList.add('u-not-allowed');
        }
    }

    /**
     * Finds forms on page, sets eventListeners, defines forms in formsErrorMapManager.
     */
    function setupFormValidation() {
        const allForms = document.querySelectorAll("form");

        const allFormControlInputs = document.querySelectorAll(`form .${_formControlClass} ${_formControlInputClass}`);

        allForms.forEach((form) => {
            formsErrorMapManager.define(form);

            const submit = form.querySelector("input[type='submit']");
            if (submit) {
                submit.addEventListener("click", checkAllInputs);
            }
        });

        allFormControlInputs.forEach((formControlInput) => {
            formControlInput.addEventListener("change", manageFormControlInputFocus);
        });
    }

    document.addEventListener("DOMContentLoaded", setupFormValidation);

})();